import { useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { TrackingEvents, track } from "../../../../util/analytics.util";
import { WebOnboardingStep } from "../../WebOnboarding.util";
import { useWebOnboardingSteps } from "../../hooks/useWebOnboardingSteps";
import { Loading, StepCounter, StepLayout } from "../../shared-components";
import { setError } from "../../utils/setError";
import { FlexMeetingSlide } from "./FlexMeetingSlide";
import { FlexMeetingSlideDocument } from "./__generated__/FlexMeetingSlide.generated";
import { parseOneOnOnes, parseSuggestions } from "./utils/parseOneOnOnesOnboarding";
import { FlexMeetingEventMap, getFlexMeetingMapFromSuggestionList } from "./utils/suggestion-utils";

export const FlexMeetingStep = ({ orgId }: { orgId: string }) => {
  const {
    goForward,
    goBack,
    shouldShowSecondaryButton,
    skipStep,
    backFromPreviousSlide,
  } = useWebOnboardingSteps();
  const [
    suggestedFlexMeetingMap,
    setSuggestedFlexMeetingMap,
  ] = useState<FlexMeetingEventMap | null>(null);
  const { data, loading } = useQuery(FlexMeetingSlideDocument, {
    variables: {
      orgRelayId: orgId,
    },
    fetchPolicy: "no-cache",
    onError: (error) => {
      setError({
        error: error,
        message: "failed to fetch flex meeting suggestions",
        showUserMessage: false,
      });
      goForward();
    },
  });

  // if returning to step don't parse out already enabled
  useEffect(() => {
    if (data) {
      const filteredSuggestions = parseSuggestions(data, backFromPreviousSlide);
      const filteredOneOnOnes = parseOneOnOnes(data, backFromPreviousSlide);

      const filteredEvents = [...filteredOneOnOnes, ...filteredSuggestions];
      if (filteredEvents.length < 1) {
        track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_NO_SUGGESTIONS);
        goForward();
        skipStep(WebOnboardingStep.FlexMeeting, true);
      } else {
        const suggestedMap = getFlexMeetingMapFromSuggestionList(filteredEvents);
        setSuggestedFlexMeetingMap(suggestedMap);
        track(TrackingEvents.WEB_ONBOARDING.FLEX_MEETING_SUGGESTIONS, {
          suggestionCount: filteredEvents.length,
        });
      }
    }
  }, [data]);

  return loading || !suggestedFlexMeetingMap ? (
    <Loading />
  ) : (
    <StepLayout>
      <StepCounter />
      <FlexMeetingSlide
        orgId={orgId}
        showSecondaryButton={shouldShowSecondaryButton}
        onClickPrimaryButton={goForward}
        onClickSecondaryButton={goBack}
        eventMap={suggestedFlexMeetingMap}
        canSetDisabled={backFromPreviousSlide}
      />
    </StepLayout>
  );
};
